export interface RouteDaySpot {
  id: string;
  name: string;
  placeId?: string; // 关联 placesData 中的地点ID，有则允许跳转详情
  type: 'place' | 'meal' | 'hotel' | 'advice' | 'transport';
  isRealPlace: boolean;
  timeRange: string;
  duration: string;
  nextDrive?: string;
  nextDriveDuration?: string;
  tips: string;
  photoSpots?: string[];
  specialties?: string[];
  status: 'not-started' | 'in-progress' | 'completed' | 'skipped';
}

export interface RouteDayAdvice {
  departure: string;
  transport: string;
  accommodation: string;
  intensity: string;
  notes: string[];
}

export interface RouteDay {
  day: number;
  title: string;
  advice: RouteDayAdvice;
  spots: RouteDaySpot[];
}

export interface RouteInfo {
  id: string;
  name: string;
  days: number;
  crowd: string;
  intensity: string;
  spotsCount: number;
  driveTime: string;
  daysPlan: RouteDay[];
}

/** 只统计真实游玩点位（非建议/转场） */
export function countRealSpots(route: RouteInfo): number {
  return route.daysPlan
    .flatMap((d) => d.spots)
    .filter((s) => s.isRealPlace).length;
}

// ==========================================
// 一日游路线
// ==========================================
export const oneDayRoute: RouteInfo = {
  id: 'route-1day',
  name: '安吉一日游精华版',
  days: 1,
  crowd: '亲子/朋友',
  intensity: '紧凑',
  spotsCount: 0,
  driveTime: '约1.5小时',
  daysPlan: [
    {
      day: 1,
      title: 'Day 1：茶园·咖啡·古村',
      advice: {
        departure: '杭州出发建议07:30前，上海出发建议06:30前',
        transport: '自驾优先，景点间车程20-30分钟',
        accommodation: '今日不住宿，建议16:30前返程',
        intensity: '紧凑，总步行约2小时，穿舒适运动鞋',
        notes: [
          '行程紧凑，建议精简拍照时间',
          '老李家土菜馆午餐高峰排队，建议11:30前到店',
          '白茶观景平台清晨光线最好',
        ],
      },
      spots: [
        {
          id: 'od-white-tea',
          name: '白茶观景平台',
          placeId: 'white-tea-view',
          type: 'place',
          isRealPlace: true,
          timeRange: '09:00-10:30',
          duration: '建议停留 1.5小时',
          nextDrive: '约30分钟车程',
          nextDriveDuration: '10:30-11:00',
          tips: '清晨光线好，适合拍照，可体验采茶。春季采茶季最美。',
          photoSpots: ['茶园全景', '观景平台', '采茶体验区'],
          status: 'not-started',
        },
        {
          id: 'od-deep-blue',
          name: '深蓝计划',
          placeId: 'deep-blue',
          type: 'place',
          isRealPlace: true,
          timeRange: '11:00-12:30',
          duration: '建议停留 1.5小时',
          nextDrive: '约20分钟车程',
          nextDriveDuration: '12:30-12:50',
          tips: '早到人少，拍照效果好。咖啡口味一般，重在场景和拍照体验。',
          photoSpots: ['矿坑湖面', '白色建筑外立面', '咖啡杯与湖面同框', '高处平台'],
          status: 'not-started',
        },
        {
          id: 'od-laolijia',
          name: '老李家土菜馆',
          placeId: 'laolijia',
          type: 'meal',
          isRealPlace: true,
          timeRange: '12:50-14:00',
          duration: '建议停留 1小时10分',
          nextDrive: '约20分钟车程',
          nextDriveDuration: '14:00-14:20',
          tips: '本地人常去的土菜馆，招牌土鸡汤非常鲜美。节假日建议提前订位。分量足，点菜不要贪多。',
          specialties: ['土鸡汤', '鲜笋煲', '杂鱼煲', '农家红烧肉'],
          status: 'not-started',
        },
        {
          id: 'od-yucun',
          name: '余村',
          placeId: 'yucun',
          type: 'place',
          isRealPlace: true,
          timeRange: '14:20-16:00',
          duration: '建议停留 1.5小时',
          tips: '美丽乡村漫步，感受两山理念发源地。村子不大步行即可。',
          photoSpots: ['村口石碑', '荷花池', '竹林步道', '两山理念会址'],
          status: 'not-started',
        },
        {
          id: 'od-return',
          name: '返程建议',
          type: 'advice',
          isRealPlace: false,
          timeRange: '16:00后',
          duration: '自由安排',
          tips: '建议16:00前出发返程，避开晚高峰。回杭州约1.5小时，回上海约2.5小时。',
          status: 'not-started',
        },
      ],
    },
  ],
};
oneDayRoute.spotsCount = countRealSpots(oneDayRoute);

// ==========================================
// 两天一夜路线
// ==========================================
export const twoDayRoute: RouteInfo = {
  id: 'route-2day',
  name: '安吉两天一夜周末游',
  days: 2,
  crowd: '亲子/情侣',
  intensity: '轻松',
  spotsCount: 0,
  driveTime: '约2小时',
  daysPlan: [
    {
      day: 1,
      title: 'Day 1：茶园·山谷·乡村',
      advice: {
        departure: '周六建议08:00前出发，避开出城高峰',
        transport: '自驾优先，景点间车程15-25分钟',
        accommodation: '今晚建议住余村/灵峰/安吉城区，方便第二天行程',
        intensity: '中等，总步行约2小时，注意防晒',
        notes: [
          '今日以茶园和乡村为主，节奏舒缓',
          '白茶观景平台清晨光线最好',
          '小杭坑山谷温度较低，带薄外套',
        ],
      },
      spots: [
        {
          id: 'td1-white-tea',
          name: '白茶观景平台',
          placeId: 'white-tea-view',
          type: 'place',
          isRealPlace: true,
          timeRange: '09:00-10:30',
          duration: '建议停留 1.5小时',
          nextDrive: '约25分钟车程',
          nextDriveDuration: '10:30-10:55',
          tips: '清晨光线好，适合拍照，可体验采茶。春季采茶季最美。',
          photoSpots: ['茶园全景', '观景平台', '采茶体验区'],
          status: 'not-started',
        },
        {
          id: 'td1-xiaohangkeng',
          name: '小杭坑',
          placeId: 'xiaohangkeng',
          type: 'place',
          isRealPlace: true,
          timeRange: '11:00-13:30',
          duration: '建议停留 2.5小时',
          nextDrive: '约20分钟车程',
          nextDriveDuration: '13:30-13:50',
          tips: '山谷营地休息用餐，可自带简餐或在营地用餐。山谷信号不好，提前下载地图。',
          photoSpots: ['山谷营地', '溪流浅滩', '晨雾竹林'],
          status: 'not-started',
        },
        {
          id: 'td1-yucun',
          name: '余村',
          placeId: 'yucun',
          type: 'place',
          isRealPlace: true,
          timeRange: '14:00-16:30',
          duration: '建议停留 2.5小时',
          tips: '美丽乡村漫步，感受两山理念发源地。村子不大步行即可，周边有农家乐可晚餐。',
          photoSpots: ['村口石碑', '荷花池', '竹林步道', '两山理念会址'],
          status: 'not-started',
        },
        {
          id: 'td1-evening',
          name: '入住/晚餐建议',
          type: 'advice',
          isRealPlace: false,
          timeRange: '17:00后',
          duration: '自由安排',
          tips: '余村附近有农家乐，县城选择更多。推荐住递铺或灵峰，方便第二天行程。',
          status: 'not-started',
        },
      ],
    },
    {
      day: 2,
      title: 'Day 2：咖啡·乐园·返程',
      advice: {
        departure: '周日建议08:00出发，早去咖啡人少',
        transport: '自驾，景点间车程20-25分钟',
        accommodation: '今日不住宿，建议16:00前出发返程',
        intensity: '中等偏上，中南百草原面积大建议租电瓶车',
        notes: [
          '瀑布咖啡早去人少，拍照效果好',
          '午餐老李家分量足，少点一些',
          '中南百草原集动物、植物、游乐园于一体，适合带娃',
        ],
      },
      spots: [
        {
          id: 'td2-waterfall',
          name: '瀑布咖啡',
          placeId: 'waterfall-coffee',
          type: 'place',
          isRealPlace: true,
          timeRange: '09:00-10:30',
          duration: '建议停留 1.5小时',
          nextDrive: '约20分钟车程',
          nextDriveDuration: '10:30-10:50',
          tips: '山谷内温度较低，建议带薄外套。雨季瀑布水量大体验最佳，蚊虫多备好驱蚊用品。',
          photoSpots: ['瀑布前石阶', '山谷全景', '咖啡露台', '溪流旁木桥'],
          status: 'not-started',
        },
        {
          id: 'td2-laolijia',
          name: '老李家土菜馆',
          placeId: 'laolijia',
          type: 'meal',
          isRealPlace: true,
          timeRange: '11:00-12:10',
          duration: '建议停留 1小时10分',
          nextDrive: '约15分钟车程',
          nextDriveDuration: '12:10-12:25',
          tips: '本地人常去的土菜馆，招牌土鸡汤非常鲜美。节假日建议提前订位。分量足，点菜不要贪多。',
          specialties: ['土鸡汤', '鲜笋煲', '杂鱼煲', '农家红烧肉'],
          status: 'not-started',
        },
        {
          id: 'td2-zhongnan',
          name: '中南百草原',
          placeId: 'zhongnan-baicaoyuan',
          type: 'place',
          isRealPlace: true,
          timeRange: '12:30-16:00',
          duration: '建议停留 3.5小时',
          tips: '集动物园、植物园、游乐园于一体，面积很大建议租电瓶车。重点看动物区和游乐园，适合带小朋友。',
          photoSpots: ['狮虎表演区', '植物园温室', '摩天轮', '跑马场'],
          status: 'not-started',
        },
        {
          id: 'td2-return',
          name: '返程建议',
          type: 'advice',
          isRealPlace: false,
          timeRange: '16:00后',
          duration: '自由安排',
          tips: '建议16:00前出发返程，避开晚高峰和周日返程大车流。回杭州约1.5小时，回上海约2.5小时。',
          status: 'not-started',
        },
      ],
    },
  ],
};
twoDayRoute.spotsCount = countRealSpots(twoDayRoute);

// ==========================================
// 三天两夜路线（默认）
// ==========================================
export const threeDayRoute: RouteInfo = {
  id: 'route-3day-family',
  name: '安吉三天两晚经典游',
  days: 3,
  crowd: '亲子/朋友',
  intensity: '轻松',
  spotsCount: 0,
  driveTime: '约3.5小时',
  daysPlan: [
    {
      day: 1,
      title: 'Day 1：茶园与山谷',
      advice: {
        departure: '杭州出发建议08:00前，上海出发建议07:30前',
        transport: '自驾优先，安吉各景点间距离不远但公共交通不便',
        accommodation: '今晚建议住余村/灵峰/安吉城区，方便第二天咖啡和亲子路线',
        intensity: '中等，总步行约2小时，注意防晒',
        notes: [
          '今日以茶园和乡村为主，节奏舒缓',
          '白茶观景平台清晨光线最好',
          '小杭坑山谷温度较低，带薄外套',
        ],
      },
      spots: [
        {
          id: 'white-tea-view',
          name: '白茶观景平台',
          placeId: 'white-tea-view',
          type: 'place',
          isRealPlace: true,
          timeRange: '09:00-10:30',
          duration: '建议停留 1.5小时',
          nextDrive: '约25分钟车程',
          nextDriveDuration: '10:30-10:55',
          tips: '清晨光线好，适合拍照，可体验采茶。春季采茶季最美。',
          photoSpots: ['茶园全景', '观景平台', '采茶体验区'],
          status: 'completed',
        },
        {
          id: 'xiaohangkeng',
          name: '小杭坑',
          placeId: 'xiaohangkeng',
          type: 'place',
          isRealPlace: true,
          timeRange: '11:00-13:30',
          duration: '建议停留 2.5小时',
          nextDrive: '约20分钟车程',
          nextDriveDuration: '13:30-13:50',
          tips: '山谷营地休息用餐，可自带简餐或在营地用餐。山谷信号不好，提前下载地图。',
          photoSpots: ['山谷营地', '溪流浅滩', '晨雾竹林'],
          status: 'completed',
        },
        {
          id: 'yucun',
          name: '余村',
          placeId: 'yucun',
          type: 'place',
          isRealPlace: true,
          timeRange: '14:00-16:30',
          duration: '建议停留 2.5小时',
          nextDrive: '约30分钟车程',
          nextDriveDuration: '16:30-17:00',
          tips: '美丽乡村漫步，感受两山理念发源地。村子不大步行即可，周边有农家乐可晚餐。',
          photoSpots: ['村口石碑', '荷花池', '竹林步道', '两山理念会址'],
          status: 'completed',
        },
        {
          id: 'd1-evening',
          name: '入住/晚餐建议',
          type: 'advice',
          isRealPlace: false,
          timeRange: '17:00后',
          duration: '自由安排',
          tips: '余村附近有农家乐，县城选择更多。推荐住递铺或灵峰，方便第二天行程。',
          status: 'not-started',
        },
      ],
    },
    {
      day: 2,
      title: 'Day 2：咖啡与自然',
      advice: {
        departure: '今天以咖啡和亲子为主，建议08:00出发',
        transport: '自驾，景点间车程20-35分钟',
        accommodation: '今晚建议住递铺/县城附近，方便第三天返程',
        intensity: '中等偏上，中南百草原面积大建议租电瓶车',
        notes: [
          '瀑布咖啡和深蓝计划早去人少，拍照效果好',
          '午餐老李家分量足，少点一些',
          '中南百草原集动物、植物、游乐园于一体，适合带娃',
        ],
      },
      spots: [
        {
          id: 'deep-blue',
          name: '深蓝计划',
          placeId: 'deep-blue',
          type: 'place',
          isRealPlace: true,
          timeRange: '09:00-10:30',
          duration: '建议停留 1.5小时',
          nextDrive: '约35分钟车程',
          nextDriveDuration: '10:30-11:05',
          tips: '早到人少，拍照效果好。咖啡口味一般，重在场景和拍照体验。周末和节假日排队严重。',
          photoSpots: ['矿坑湖面', '白色建筑外立面', '咖啡杯与湖面同框', '高处平台'],
          status: 'in-progress',
        },
        {
          id: 'waterfall-coffee',
          name: '瀑布咖啡',
          placeId: 'waterfall-coffee',
          type: 'place',
          isRealPlace: true,
          timeRange: '11:10-12:30',
          duration: '建议停留 1小时20分',
          nextDrive: '约20分钟车程',
          nextDriveDuration: '12:30-12:50',
          tips: '山谷内温度较低，建议带薄外套。雨季瀑布水量大体验最佳，蚊虫多备好驱蚊用品。',
          photoSpots: ['瀑布前石阶', '山谷全景', '咖啡露台', '溪流旁木桥'],
          status: 'not-started',
        },
        {
          id: 'laolijia',
          name: '老李家土菜馆',
          placeId: 'laolijia',
          type: 'meal',
          isRealPlace: true,
          timeRange: '12:50-14:00',
          duration: '建议停留 1小时10分',
          nextDrive: '约15分钟车程',
          nextDriveDuration: '14:00-14:15',
          tips: '本地人常去的土菜馆，招牌土鸡汤非常鲜美。节假日建议提前订位。分量足，点菜不要贪多。',
          specialties: ['土鸡汤', '鲜笋煲', '杂鱼煲', '农家红烧肉'],
          status: 'not-started',
        },
        {
          id: 'zhongnan-baicaoyuan',
          name: '中南百草原',
          placeId: 'zhongnan-baicaoyuan',
          type: 'place',
          isRealPlace: true,
          timeRange: '14:15-17:00',
          duration: '建议停留 2小时45分',
          nextDrive: '约20分钟车程',
          nextDriveDuration: '17:00-17:20',
          tips: '集动物园、植物园、游乐园于一体，面积很大建议租电瓶车。重点看动物区和游乐园，适合带小朋友。',
          photoSpots: ['狮虎表演区', '植物园温室', '摩天轮', '跑马场'],
          status: 'not-started',
        },
        {
          id: 'd2-evening',
          name: '晚餐/入住建议',
          type: 'advice',
          isRealPlace: false,
          timeRange: '17:20后',
          duration: '自由安排',
          tips: '县城选择多，阿露佳也是不错的选择。今晚建议住县城，方便第三天返程路线。',
          status: 'not-started',
        },
      ],
    },
    {
      day: 3,
      title: 'Day 3：竹林与返程',
      advice: {
        departure: '最后一天轻松返程路线，建议08:30出发',
        transport: '自驾，景点间车程15-25分钟',
        accommodation: '今日不住宿，建议16:30前出发返程',
        intensity: '轻松，总步行约1.5小时，穿运动鞋',
        notes: [
          '大竹海环线1.5小时，穿运动鞋',
          '天荒坪农家乐是本地人常吃的地道土菜',
          '竹博园可以看大熊猫，非常适合小朋友',
          '阿露佳在县城，是返程前晚餐备选，想早点走可以跳过',
        ],
      },
      spots: [
        {
          id: 'bamboo-sea',
          name: '中国大竹海',
          placeId: 'bamboo-sea',
          type: 'place',
          isRealPlace: true,
          timeRange: '08:30-10:30',
          duration: '建议停留 2小时',
          nextDrive: '约20分钟车程',
          nextDriveDuration: '10:30-10:50',
          tips: '电影《卧虎藏龙》取景地，竹林很壮观。建议走环线全程约1.5小时，夏季竹林内很凉爽。',
          photoSpots: ['竹海长廊', '卧虎藏龙取景地', '山顶观景台', '竹林小道'],
          status: 'not-started',
        },
        {
          id: 'lunch-d3',
          name: '天荒坪农家乐',
          placeId: 'tianhuangping-food',
          type: 'meal',
          isRealPlace: true,
          timeRange: '11:00-12:30',
          duration: '建议停留 1小时30分',
          nextDrive: '约20分钟车程',
          nextDriveDuration: '12:30-12:50',
          tips: '大竹海附近有很多地道农家乐，午餐在这里解决。竹香土鸡锅、鲜笋煲是当地必吃菜。',
          specialties: ['竹香土鸡锅', '安吉鲜笋煲', '溪鱼', '农家土菜'],
          status: 'not-started',
        },
        {
          id: 'bamboo-park',
          name: '安吉竹博园',
          placeId: 'bamboo-park',
          type: 'place',
          isRealPlace: true,
          timeRange: '13:00-15:00',
          duration: '建议停留 2小时',
          nextDrive: '约20分钟车程',
          nextDriveDuration: '15:00-15:20',
          tips: '可以看大熊猫和小熊猫，非常适合带小朋友科普教育。景区内有竹制品商店，夏季竹林凉爽宜人。',
          photoSpots: ['熊猫馆', '竹林迷宫', '竹文化博物馆', '竹筏漂流'],
          status: 'not-started',
        },
        {
          id: 'alujia-d3',
          name: '阿露佳·舌尖上的安吉菜',
          placeId: 'alujia',
          type: 'meal',
          isRealPlace: true,
          timeRange: '15:30-16:30',
          duration: '建议停留 1小时',
          tips: '县城创意菜代表，竹香土鸡锅是必点。这里在返程顺路，是返程前晚餐/补给备选，想早点走可以跳过。',
          specialties: ['竹香土鸡锅', '安吉鲜笋煲', '杂鱼煲', '茶叶大虾'],
          status: 'not-started',
        },
        {
          id: 'd3-return',
          name: '返程建议',
          type: 'advice',
          isRealPlace: false,
          timeRange: '16:30后',
          duration: '自由安排',
          tips: '建议16:30前出发返程，避开晚高峰。回杭州约1.5小时，回上海约2.5小时。',
          status: 'not-started',
        },
      ],
    },
  ],
};
threeDayRoute.spotsCount = countRealSpots(threeDayRoute);

// ==========================================
// 4-5天深度游路线
// ==========================================
export const longRoute: RouteInfo = {
  id: 'route-4day',
  name: '安吉四天三晚深度游',
  days: 4,
  crowd: '亲子/朋友',
  intensity: '中等',
  spotsCount: 0,
  driveTime: '约4小时',
  daysPlan: [
    {
      day: 1,
      title: 'Day 1：茶园·山谷·乡村',
      advice: {
        departure: '建议08:00前出发，避开出城高峰',
        transport: '自驾优先，景点间车程15-25分钟',
        accommodation: '今晚建议住余村/灵峰/安吉城区',
        intensity: '中等，总步行约2小时',
        notes: [
          '今日以茶园和乡村为主，节奏舒缓',
          '白茶观景平台清晨光线最好',
          '小杭坑山谷温度较低，带薄外套',
        ],
      },
      spots: [
        {
          id: 'ld1-white-tea',
          name: '白茶观景平台',
          placeId: 'white-tea-view',
          type: 'place',
          isRealPlace: true,
          timeRange: '09:00-10:30',
          duration: '建议停留 1.5小时',
          nextDrive: '约25分钟车程',
          nextDriveDuration: '10:30-10:55',
          tips: '清晨光线好，适合拍照，可体验采茶。春季采茶季最美。',
          photoSpots: ['茶园全景', '观景平台', '采茶体验区'],
          status: 'not-started',
        },
        {
          id: 'ld1-xiaohangkeng',
          name: '小杭坑',
          placeId: 'xiaohangkeng',
          type: 'place',
          isRealPlace: true,
          timeRange: '11:00-13:30',
          duration: '建议停留 2.5小时',
          nextDrive: '约20分钟车程',
          nextDriveDuration: '13:30-13:50',
          tips: '山谷营地休息用餐，可自带简餐或在营地用餐。山谷信号不好，提前下载地图。',
          photoSpots: ['山谷营地', '溪流浅滩', '晨雾竹林'],
          status: 'not-started',
        },
        {
          id: 'ld1-yucun',
          name: '余村',
          placeId: 'yucun',
          type: 'place',
          isRealPlace: true,
          timeRange: '14:00-16:30',
          duration: '建议停留 2.5小时',
          tips: '美丽乡村漫步，感受两山理念发源地。村子不大步行即可，周边有农家乐可晚餐。',
          photoSpots: ['村口石碑', '荷花池', '竹林步道', '两山理念会址'],
          status: 'not-started',
        },
        {
          id: 'ld1-evening',
          name: '入住/晚餐建议',
          type: 'advice',
          isRealPlace: false,
          timeRange: '17:00后',
          duration: '自由安排',
          tips: '余村附近有农家乐，县城选择更多。推荐住递铺或灵峰，方便第二天行程。',
          status: 'not-started',
        },
      ],
    },
    {
      day: 2,
      title: 'Day 2：咖啡与高山',
      advice: {
        departure: '今天以咖啡和云上草原为主，建议08:00出发',
        transport: '自驾，景点间车程30-40分钟',
        accommodation: '今晚建议住递铺/县城附近',
        intensity: '中等偏上，云上草原海拔高注意温差',
        notes: [
          '深蓝计划和瀑布咖啡早去人少',
          '云上草原海拔1168米，温差大需带外套',
          '索道排队时间长，建议早到',
        ],
      },
      spots: [
        {
          id: 'ld2-deep-blue',
          name: '深蓝计划',
          placeId: 'deep-blue',
          type: 'place',
          isRealPlace: true,
          timeRange: '09:00-10:30',
          duration: '建议停留 1.5小时',
          nextDrive: '约35分钟车程',
          nextDriveDuration: '10:30-11:05',
          tips: '早到人少，拍照效果好。咖啡口味一般，重在场景和拍照体验。',
          photoSpots: ['矿坑湖面', '白色建筑外立面', '咖啡杯与湖面同框', '高处平台'],
          status: 'not-started',
        },
        {
          id: 'ld2-waterfall',
          name: '瀑布咖啡',
          placeId: 'waterfall-coffee',
          type: 'place',
          isRealPlace: true,
          timeRange: '11:10-12:30',
          duration: '建议停留 1小时20分',
          nextDrive: '约40分钟车程',
          nextDriveDuration: '12:30-13:10',
          tips: '山谷内温度较低，建议带薄外套。雨季瀑布水量大体验最佳。',
          photoSpots: ['瀑布前石阶', '山谷全景', '咖啡露台', '溪流旁木桥'],
          status: 'not-started',
        },
        {
          id: 'ld2-cloud',
          name: '云上草原',
          placeId: 'cloud-prairie',
          type: 'place',
          isRealPlace: true,
          timeRange: '13:30-17:00',
          duration: '建议停留 3.5小时',
          tips: '海拔1168米，温差大需带外套。索道排队时间长，建议早到。部分项目有身高体重限制。',
          photoSpots: ['草原全景', '悬崖秋千', '彩虹滑草道', '山顶云海'],
          status: 'not-started',
        },
        {
          id: 'ld2-evening',
          name: '晚餐/入住建议',
          type: 'advice',
          isRealPlace: false,
          timeRange: '17:00后',
          duration: '自由安排',
          tips: '县城选择多，阿露佳也是不错的选择。今晚建议住县城，方便第三天行程。',
          status: 'not-started',
        },
      ],
    },
    {
      day: 3,
      title: 'Day 3：亲子乐园',
      advice: {
        departure: '今天以亲子为主，建议08:30出发',
        transport: '自驾，景点间车程15-20分钟',
        accommodation: '今晚建议住县城附近',
        intensity: '轻松，中南百草原可租电瓶车',
        notes: [
          '中南百草原面积大，建议租电瓶车',
          '自然博物院需提前在公众号预约',
          '馆内有餐厅，也可以自带食物',
        ],
      },
      spots: [
        {
          id: 'ld3-zhongnan',
          name: '中南百草原',
          placeId: 'zhongnan-baicaoyuan',
          type: 'place',
          isRealPlace: true,
          timeRange: '09:00-14:00',
          duration: '建议停留 5小时',
          nextDrive: '约20分钟车程',
          nextDriveDuration: '14:00-14:20',
          tips: '集动物园、植物园、游乐园于一体，面积很大建议租电瓶车。重点看动物区和游乐园。',
          photoSpots: ['狮虎表演区', '植物园温室', '摩天轮', '跑马场'],
          status: 'not-started',
        },
        {
          id: 'ld3-museum',
          name: '浙江自然博物院安吉馆',
          placeId: 'nature-museum',
          type: 'place',
          isRealPlace: true,
          timeRange: '14:30-17:00',
          duration: '建议停留 2.5小时',
          tips: '亚洲最大自然博物馆之一，需提前在公众号预约。非常适合雨天游玩。',
          photoSpots: ['恐龙化石展厅', '海洋展厅', '生态馆穹顶', '4D影院入口'],
          status: 'not-started',
        },
        {
          id: 'ld3-evening',
          name: '晚餐/入住建议',
          type: 'advice',
          isRealPlace: false,
          timeRange: '17:00后',
          duration: '自由安排',
          tips: '县城选择多，老李家土菜馆是不错的选择。',
          status: 'not-started',
        },
      ],
    },
    {
      day: 4,
      title: 'Day 4：竹林与返程',
      advice: {
        departure: '最后一天轻松返程路线，建议08:30出发',
        transport: '自驾，景点间车程15-25分钟',
        accommodation: '今日不住宿，建议16:30前出发返程',
        intensity: '轻松，总步行约1.5小时，穿运动鞋',
        notes: [
          '大竹海环线1.5小时，穿运动鞋',
          '竹博园可以看大熊猫，非常适合小朋友',
          '阿露佳在县城，是返程前晚餐备选',
        ],
      },
      spots: [
        {
          id: 'ld4-bamboo-sea',
          name: '中国大竹海',
          placeId: 'bamboo-sea',
          type: 'place',
          isRealPlace: true,
          timeRange: '08:30-10:30',
          duration: '建议停留 2小时',
          nextDrive: '约20分钟车程',
          nextDriveDuration: '10:30-10:50',
          tips: '电影《卧虎藏龙》取景地，竹林很壮观。建议走环线全程约1.5小时。',
          photoSpots: ['竹海长廊', '卧虎藏龙取景地', '山顶观景台', '竹林小道'],
          status: 'not-started',
        },
        {
          id: 'ld4-lunch',
          name: '天荒坪农家乐',
          placeId: 'tianhuangping-food',
          type: 'meal',
          isRealPlace: true,
          timeRange: '11:00-12:30',
          duration: '建议停留 1小时30分',
          nextDrive: '约20分钟车程',
          nextDriveDuration: '12:30-12:50',
          tips: '大竹海附近有很多地道农家乐，午餐在这里解决。竹香土鸡锅、鲜笋煲是当地必吃菜。',
          specialties: ['竹香土鸡锅', '安吉鲜笋煲', '溪鱼', '农家土菜'],
          status: 'not-started',
        },
        {
          id: 'ld4-bamboo-park',
          name: '安吉竹博园',
          placeId: 'bamboo-park',
          type: 'place',
          isRealPlace: true,
          timeRange: '13:00-15:00',
          duration: '建议停留 2小时',
          nextDrive: '约20分钟车程',
          nextDriveDuration: '15:00-15:20',
          tips: '可以看大熊猫和小熊猫，非常适合带小朋友科普教育。夏季竹林凉爽宜人。',
          photoSpots: ['熊猫馆', '竹林迷宫', '竹文化博物馆', '竹筏漂流'],
          status: 'not-started',
        },
        {
          id: 'ld4-alujia',
          name: '阿露佳·舌尖上的安吉菜',
          placeId: 'alujia',
          type: 'meal',
          isRealPlace: true,
          timeRange: '15:30-16:30',
          duration: '建议停留 1小时',
          tips: '县城创意菜代表，竹香土鸡锅是必点。返程前晚餐/补给备选，想早点走可以跳过。',
          specialties: ['竹香土鸡锅', '安吉鲜笋煲', '杂鱼煲', '茶叶大虾'],
          status: 'not-started',
        },
        {
          id: 'ld4-return',
          name: '返程建议',
          type: 'advice',
          isRealPlace: false,
          timeRange: '16:30后',
          duration: '自由安排',
          tips: '建议16:30前出发返程，避开晚高峰。回杭州约1.5小时，回上海约2.5小时。',
          status: 'not-started',
        },
      ],
    },
  ],
};
longRoute.spotsCount = countRealSpots(longRoute);

// ==========================================
// 路线模板映射 + 默认导出
// ==========================================
export const routeTemplates: Record<string, RouteInfo> = {
  '一日游': oneDayRoute,
  '两天一夜': twoDayRoute,
  '三天两夜': threeDayRoute,
  '4-5天': longRoute,
};

export const defaultRoute = threeDayRoute;

// 兼容旧接口导出
export const myTripProgress = {
  routeName: defaultRoute.name,
  currentDay: 2,
  totalSpots: defaultRoute.spotsCount,
  completedSpots: 3,
  todayRemaining: 4,
  nextSpot: '深蓝计划',
  nextSpotId: 'deep-blue',
};

// 兼容旧 selectedSpots 结构（后续移除）
export const selectedSpots = [
  { id: 'white-tea-view', name: '白茶观景平台', status: 'completed' as const },
  { id: 'xiaohangkeng', name: '小杭坑', status: 'completed' as const },
  { id: 'yucun', name: '余村', status: 'completed' as const },
  { id: 'deep-blue', name: '深蓝计划', status: 'in-progress' as const },
  { id: 'waterfall-coffee', name: '瀑布咖啡', status: 'not-started' as const },
  { id: 'laolijia', name: '老李家土菜馆', status: 'not-started' as const },
  { id: 'zhongnan-baicaoyuan', name: '中南百草原', status: 'not-started' as const },
  { id: 'bamboo-sea', name: '中国大竹海', status: 'not-started' as const },
  { id: 'lunch-d3', name: '天荒坪农家乐', status: 'not-started' as const },
  { id: 'bamboo-park', name: '安吉竹博园', status: 'not-started' as const },
  { id: 'alujia-d3', name: '阿露佳·舌尖上的安吉菜', status: 'not-started' as const },
];